import { Formik, FormikProps } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import React, { useState } from 'react';
import uuid from 'uuid';
import * as yup from 'yup';
import { registerDevice, RegisterDeviceContracts } from '../Async/RegisterDevicesAsync';
import { Button, ButtonMode } from '../Components/Button';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { StyledPrompt } from '../Components/Prompts/StyledPrompt';
import { displayError, getConfig } from '../Config/ApplicationConfig';
import { requiredField, ServicesAustraliaLocale } from '../ServicesAustralia.en';
import { convertValidationResultToErrorsObjectV2, isDomainValidationError } from '../Shared/DomainValidationFunctions';
import { isNetworkResponse } from '../Shared/HttpRequestExecutor';
import { RegisterDeviceFields } from './RegisterDeviceFields';
import styles from './RegisterDeviceForm.module.scss';

export type DialogProps = {
    onSuccess(result: RegisterDeviceContracts.Response.DeviceCreateResult, form: RegisterDeviceFormState): void;
    initialValues?: RegisterDeviceFormState;
};

export type RegisterDeviceFormState = {
    customerName: string;
    customerAuthSystem: string;
    customerAuthSystemKey: string;
    deviceName: string;
    deviceActivationCode: string;
    deviceOrganisationIdentifier: string;
    deviceAuthoringSystem: string;
    deviceAuthoringSystemKey: string;
    deviceQueueName: string;
    healthSystemApplicationName: string;
    healthSystemApplicationClientIdentifier: string;
    healthSystemApplicationXIBMClientIdentifier: string;
};

const validationSchema = yup.object<RegisterDeviceFormState>({
    customerName: yup.string().required(requiredField),
    customerAuthSystem: yup.string().required(requiredField),
    customerAuthSystemKey: yup.string().required(requiredField),
    deviceName: yup.string().required(requiredField),
    deviceActivationCode: yup.string().required(requiredField),
    deviceOrganisationIdentifier: yup.string().required(requiredField),
    deviceAuthoringSystem: yup.string().required(requiredField),
    deviceAuthoringSystemKey: yup.string().required(requiredField),
    deviceQueueName: yup
        .string()
        .max(150, 'Max length 150')
        .min(5, 'Min length 5')
        .test('start-valid', 'Can not start with /', function (value: string) {
            if (!!value) {
                return !value.startsWith('/');
            }

            return true;
        })
        .test('end-valid', 'Can not end with /', function (value: string) {
            if (!!value) {
                return !value.endsWith('/');
            }

            return true;
        })
        .required(requiredField),
    healthSystemApplicationName: yup.string().required(requiredField),
    healthSystemApplicationClientIdentifier: yup.string().required(requiredField),
    healthSystemApplicationXIBMClientIdentifier: yup.string().required(requiredField)
});

export const RegisterDeviceForm: React.FC<DialogProps> = props => {
    const defaultFormState: RegisterDeviceFormState = {
        customerName: '',
        customerAuthSystem: '',
        customerAuthSystemKey: uuid.v4(),
        deviceName: '',
        deviceActivationCode: '',
        deviceOrganisationIdentifier: '',
        deviceAuthoringSystem: '',
        deviceAuthoringSystemKey: uuid.v4(),
        deviceQueueName: getConfig().proda.defaultQueueName,
        healthSystemApplicationName: getConfig().proda.applicationName,
        healthSystemApplicationClientIdentifier: getConfig().proda.clientIdentifier,
        healthSystemApplicationXIBMClientIdentifier: getConfig().proda.xibmClientIdentifier
    };

    async function newEncounterType(values: RegisterDeviceFormState, actions: FormikHelpers<RegisterDeviceFormState>) {
        actions.setSubmitting(true);
        const data: RegisterDeviceContracts.Request.CreateDevice = {
            customer: {
                name: values.customerName,
                authoringSystem: values.customerAuthSystem,
                authoringSystemForeignKey: values.customerAuthSystemKey
            },
            authoringSystem: values.deviceAuthoringSystem,
            authoringSystemForeignKey: values.deviceAuthoringSystemKey,
            prodaActivationCode: values.deviceActivationCode,
            prodaDeviceName: values.deviceName,
            prodaOrganisationIdentifier: values.deviceOrganisationIdentifier,
            instanceQueueName: values.deviceQueueName,
            applicationClientIdentifier: values.healthSystemApplicationClientIdentifier,
            prodaClientIdentifier: values.healthSystemApplicationXIBMClientIdentifier,
            applicationName: values.healthSystemApplicationName
        };

        try {
            const result = await registerDevice(data);
            actions.resetForm({ values, isSubmitting: false });
            props.onSuccess(result, values);
        } catch (err) {
            if (isDomainValidationError(err)) {
                const apiErrors = convertValidationResultToErrorsObjectV2<RegisterDeviceFormState>(
                    err.content,
                    ServicesAustraliaLocale,
                    path => {
                        return path;
                    }
                );
                actions.setErrors(apiErrors.fieldErrors);
                if (err.message) {
                    displayError(err.message);
                }
            } else {
                if (isNetworkResponse(err)) {
                    displayError(err.statusText);
                } else displayError((err as Error).message ?? 'Unknown error occurred.');
            }
            actions.setSubmitting(false);
        }
    }

    const [showPrompt, setShowPrompt] = useState(false);
    return (
        <Formik<RegisterDeviceFormState>
            initialValues={props.initialValues ? props.initialValues : defaultFormState}
            validationSchema={validationSchema}
            onSubmit={newEncounterType}
        >
            {(formProps: FormikProps<RegisterDeviceFormState>) => {
                return (
                    <>
                        {formProps.isSubmitting ? (
                            <div className={styles.loading}>
                                <LayoutCentral>
                                    <LoadingPlaceholder />
                                </LayoutCentral>
                            </div>
                        ) : (
                            <>
                                <RegisterDeviceFields formProps={formProps} />
                                <div className={styles.formDetails}>
                                    <Button
                                        width={150}
                                        type='submit'
                                        disabled={formProps.isSubmitting}
                                        testId='encounter-types'
                                        onClick={async e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const errors = await formProps.validateForm();
                                            const errorKeys = Object.keys(errors);
                                            if (Object.keys(errors).length === 0) {
                                                setShowPrompt(true);
                                            } else {
                                                errorKeys.forEach(errorKey =>
                                                    formProps.setFieldTouched(errorKey, true, false)
                                                );
                                            }
                                        }}
                                    >
                                        Register
                                    </Button>
                                </div>
                            </>
                        )}
                        <StyledPrompt
                            open={showPrompt}
                            title={'Are you sure you want to register?'}
                            onCancel={() => setShowPrompt(false)}
                            actions={[
                                <StyledDialogButton
                                    title='Cancel'
                                    key='confirm-cancel'
                                    mode={ButtonMode.border}
                                    width={112}
                                    onClick={() => setShowPrompt(false)}
                                    testId='confirm-cancel'
                                >
                                    Cancel
                                </StyledDialogButton>,
                                <StyledDialogButton
                                    title='Register'
                                    key='confirm-register'
                                    onClick={async () => {
                                        setShowPrompt(false);
                                        await formProps.submitForm();
                                    }}
                                    width={112}
                                    testId='confirm-register'
                                >
                                    Register
                                </StyledDialogButton>
                            ]}
                        />
                    </>
                );
            }}
        </Formik>
    );
};
